import type { LinkRequestLifetime } from '#~/api/agent/link-request-expiry.js';
import { LinkRequestLifetimeNotice, useLinkRequestLifetime } from './link-request-lifetime.jsx';
import { useRenderedBootHumanWait } from '#~/routes/boot-overlay/boot-budget-view.js';
import * as Wizard from '#~/agent/components/wizard/index.js';
import { fetchRequestLink } from '#~/api/agent/index.js';
import type { LinkRequest } from '#~/api/agent/index.js';
import type { ApiError } from '#~/api/index.js';
import { useAuth0 } from '#~/auth/index.js';
import { NotFoundError } from '#~/error/index.js';
import { Paths } from '#~/routes/index.js';
import { useUser } from '#~/user/hooks.js';
import { Hardware } from './hardware.jsx';
import { Problem } from './problem.jsx';
import { Anchor } from '@studio/design-system/button';
import { Loader } from '@studio/design-system/loader';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { unwrap } from '@studio/js-utils/async';
import { normalizeId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import { useAsyncEffect, useLatestFn } from '@studio/react-utils/hooks';
import { pick } from 'lodash-es';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

/** `<Confirm>` component. */
export const Confirm = (): ReactElement => {
  const params = useParams();
  const { getAccessTokenSilently } = useAuth0();

  const user = useUser((µ) => pick(µ, ['email']), shallow);

  const agentId = normalizeId<Agent['id']>(params.agentId);
  const requestId = normalizeId<LinkRequest['id']>(params.requestId);

  if (!agentId || !requestId) {
    throw new NotFoundError();
  }

  const [agent, setAgent] = useState<
    Omit<Agent, 'lastShutDownAt' | 'roomId' | 'status'> & LinkRequestLifetime
  >();
  const [isBusy] = useState(false);
  const [error, setError] = useState<ApiError>();
  const lifetime = useLinkRequestLifetime(agent?.linkExpiresAt, error);
  useRenderedBootHumanWait(!!agent || !!error);

  const navigate = useNavigate();

  useAsyncEffect(
    function* generator(signal: AbortSignal) {
      try {
        const token = yield* unwrap(getAccessTokenSilently());

        const response = yield* unwrap(fetchRequestLink(requestId, token));

        setAgent({ ...response, id: agentId });
      } catch (error_) {
        if (!signal.aborted) {
          setError(error_ as ApiError);
        }
      }
    },
    [requestId]
  );

  const handleLogout = useLatestFn((event: React.MouseEvent) => {
    event.preventDefault();

    navigate(`/${Paths.logout}`, {
      state: {
        redirect: window.location.href
      }
    });
  });

  const handleConfirm = useLatestFn(() => {
    if (lifetime.expired) return;
    navigate(Paths.agent.link.selectStudio.resolve(agentId, requestId));
  });

  // Let an already submitted claim settle; expiry prevents new actions.
  if (lifetime.expired && !isBusy) return <LinkRequestLifetimeNotice {...lifetime} />;

  if (!agent && !error) {
    return <Loader />;
  }

  const linkMessage = `Please confirm that you’d like to link this ${
    agent?.embedded ? 'camera' : 'production machine'
  } with your ${vendorName()} account.`;
  const linkTitle = `Link your ${agent?.embedded ? 'Camera' : 'Machine'}`;

  const continueButtonText = agent?.embedded
    ? 'Continue'
    : isBusy
      ? `Linking ${agent?.embedded ? 'Camera' : 'Machine'}...`
      : `Link ${agent?.embedded ? 'Camera' : 'Machine'}`;

  return (
    <Wizard.Root currentStep={1} totalSteps={3}>
      <LinkRequestLifetimeNotice {...lifetime} />
      {!!agent && (
        <>
          <Hardware agent={agent} />
          <Wizard.Separator />
        </>
      )}

      {!!error && (
        <Problem agent={{ ...agent, id: agentId }} error={{ ...error, title: 'Linking Error' }}>
          <Anchor
            className='text-primary'
            onClick={(): void => {
              navigate(Paths.agent.link.confirm.resolve(agentId, requestId));
            }}
          >
            Try again
          </Anchor>
        </Problem>
      )}

      {!!agent && !error && (
        <>
          <div className='px-[16px]'>
            <Wizard.Verbiage message={linkMessage} title={linkTitle} />
          </div>
          <div className='text-base font-bold leading-150 text-primary [overflow-wrap:anywhere]'>
            {user.email}
          </div>

          <Wizard.ButtonBox className='w-full px-[48px]'>
            <Wizard.Button
              busy={isBusy}
              className='w-full'
              disabled={isBusy}
              onClick={(): void => {
                handleConfirm();
              }}
            >
              {continueButtonText}
            </Wizard.Button>

            <Anchor className='text-primary' onClick={handleLogout}>
              Switch to a different account
            </Anchor>
          </Wizard.ButtonBox>
        </>
      )}
    </Wizard.Root>
  );
};
